import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Building2, CircleUser, DollarSign, Users } from 'lucide-react';

import {
  getCompanyQueryKey,
  loadQuery as queryCompanies,
} from 'common/actions/thirdPartyCompanyQueries';
import { getUserQueryKey, loadQuery as queryUsers } from 'common/actions/userQueries';
import {
  getUsersForCompanyQueryKey,
  loadUsersForCompanyQuery as queryUsersForCompany,
} from 'common/actions/usersForCompanyQueries';
import UserAvatarV2 from 'common/common/UserAvatarV2';
import { RevenueTimeframes, convertMRRToTimeframe } from 'common/company/RevenueHelpers';
import { CompanyContext } from 'common/containers/CompanyContainer';
import connect from 'common/core/connect';
import HStack from 'common/uiV3/HStack';
import { Select, type SelectBaseOption } from 'common/uiV3/Select';
import VStack from 'common/uiV3/VStack';
import { isNotNil } from 'common/util/isNil';
import numberWithCommas from 'common/util/numberWithCommas';
import useDelayer from 'common/util/useDelayer';

import type { Company } from 'common/api/endpoints/companies';
import type { User } from 'common/api/endpoints/users';
import type { Option } from 'common/ui/common/select/SelectCommon';
import type { State } from 'redux-connect';

type UserSelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedUserID: string | undefined;
};

const UserSelect = ({
  options,
  onChange,
  onSearchChange,
  selectedUserID,
  isLoading,
}: UserSelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <CircleUser /> User
      </>
    }
    value={selectedUserID}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    isLoading={isLoading}
    allowClear
  />
);

type CompanySelectProps = {
  isLoading: boolean;
  onChange: (value: string | undefined) => void;
  onSearchChange: (value: string | undefined) => Promise<SelectBaseOption[]>;
  options: SelectBaseOption[];
  selectedCompanyID: string | undefined;
};

type CompanyOptionLabelProps = {
  company: {
    name: string;
    userCount: number | null | undefined;
    monthlySpend: number | null | undefined;
  };
  revenueTimeframe: RevenueTimeframes;
  hasCollectedMonthlySpend: boolean;
};

const CompanyOptionLabel = ({
  company,
  revenueTimeframe,
  hasCollectedMonthlySpend,
}: CompanyOptionLabelProps) => {
  const userCountText = isNotNil(company.userCount) ? numberWithCommas(company.userCount) : '-';

  let revenueText = '-';
  if (isNotNil(company.monthlySpend) && hasCollectedMonthlySpend) {
    const revenue = convertMRRToTimeframe(company.monthlySpend, revenueTimeframe);
    const revenueLabel = revenueTimeframe === RevenueTimeframes.arr ? 'ARR' : 'MRR';
    revenueText = `${numberWithCommas(Math.round(revenue))} ${revenueLabel}`;
  }

  return (
    <VStack>
      <div className="truncate">{company.name}</div>
      <HStack className="items-center gap-2 text-secondary-foreground">
        <HStack className="items-center gap-1">
          <Users className="size-3" />
          <span className="text-sm">{userCountText}</span>
        </HStack>
        <HStack className="items-center gap-0.5">
          <DollarSign className="size-3" />
          <span className="text-sm">{revenueText}</span>
        </HStack>
      </HStack>
    </VStack>
  );
};

const CompanySelect = ({
  isLoading,
  options,
  onChange,
  onSearchChange,
  selectedCompanyID,
}: CompanySelectProps) => (
  <Select
    options={options}
    placeholder={
      <>
        <Building2 /> Company
      </>
    }
    isLoading={isLoading}
    value={selectedCompanyID}
    onValueChange={onChange}
    onSearchChange={onSearchChange}
    isSearchable
    fullWidth
    allowClear
    fitTrigger
  />
);

export type OnUserCompanyChange = ({
  thirdPartyCompanyID,
  userID,
}: {
  thirdPartyCompanyID: string | undefined;
  userID: string | undefined;
}) => void;

type OwnProps = {
  defaultUser?: Option;
  defaultCompany?: Option;
  onChange: OnUserCompanyChange;
};

type ConnectStateProps = {
  thirdPartyCompanies: any;
  thirdPartyCompanyQueries: any;
  users: any;
  usersForCompanyQueries: any;
  userQueries: any;
};

type ConnectDispatchProps = {
  searchCompanies: (queryParams: { search: string; userID?: string }) => void;
  searchUsers: (queryParams: { search: string }) => void;
  searchUsersForCompany: (queryParams: { search: string; thirdPartyCompanyID: string }) => void;
};

type UserCompanySelectProps = OwnProps & ConnectStateProps & ConnectDispatchProps;

const UserCompanySelect = (props: UserCompanySelectProps & OwnProps) => {
  const {
    defaultUser,
    defaultCompany,
    onChange,
    searchCompanies,
    searchUsers,
    searchUsersForCompany,
    thirdPartyCompanies,
    thirdPartyCompanyQueries,
    users,
    usersForCompanyQueries,
    userQueries,
  } = props;

  const [selectedUserID, setSelectedUserID] = useState<string | undefined>(defaultUser?.value);
  const [selectedCompanyID, setSelectedCompanyID] = useState<string | undefined>(
    defaultCompany?.value,
  );

  const [userSearchValue, setUserSearchValue] = useState(
    defaultUser?.value && !defaultCompany?.value ? defaultUser.value : '',
  );
  const [companySearchValue, setCompanySearchValue] = useState('');

  const [userLoading, setUserLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);

  const [autoAssignCompanyFlag, setAutoAssignCompanyFlag] = useState(false);

  const performUserSearch = useCallback(
    async (value: string, thirdPartyCompanyID?: string) => {
      setUserLoading(true);
      if (thirdPartyCompanyID) {
        await searchUsersForCompany({ search: value, thirdPartyCompanyID });
      } else {
        await searchUsers({ search: value });
      }
      setUserLoading(false);
    },
    [searchUsers, searchUsersForCompany, setUserLoading],
  );

  const performCompanySearch = useCallback(
    async (value: string, userID?: string) => {
      setCompanyLoading(true);
      await searchCompanies({ search: value, userID });
      setCompanyLoading(false);
    },
    [searchCompanies, setCompanyLoading],
  );

  const onUserSearchChange = useCallback(
    async (value) => {
      setUserSearchValue(value);
      performUserSearch(value, selectedCompanyID);
      return [];
    },
    [performUserSearch, selectedCompanyID],
  );

  const onCompanySearchChange = useCallback(
    async (value) => {
      setCompanySearchValue(value);
      performCompanySearch(value, selectedUserID);
    },
    [performCompanySearch, selectedUserID],
  );

  const onUserChange = useCallback(
    async (newUserID: string | undefined) => {
      setSelectedUserID(newUserID);
      setUserSearchValue('');
      onChange({ thirdPartyCompanyID: selectedCompanyID, userID: newUserID });

      if (newUserID && !selectedCompanyID) {
        performCompanySearch('', newUserID);
      }
      setAutoAssignCompanyFlag(true);
    },
    [onChange, performCompanySearch, selectedCompanyID],
  );

  const onCompanyChange = useCallback(
    async (newCompanyID: string | undefined) => {
      setSelectedCompanyID(newCompanyID);
      setCompanySearchValue('');
      onChange({ thirdPartyCompanyID: newCompanyID, userID: selectedUserID });

      if (newCompanyID && !selectedUserID) {
        performUserSearch('', newCompanyID);
      }
    },
    [onChange, performUserSearch, selectedUserID],
  );

  useEffect(() => {
    // Load initial users and companies on mount
    performUserSearch(
      selectedUserID && !selectedCompanyID ? selectedUserID : '',
      selectedCompanyID,
    );
    performCompanySearch('', selectedUserID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userQueryKey = selectedCompanyID
    ? getUsersForCompanyQueryKey({
        search: userSearchValue,
        thirdPartyCompanyID: selectedCompanyID,
      })
    : getUserQueryKey({
        search: userSearchValue,
      });
  const userQueryResult = selectedCompanyID
    ? usersForCompanyQueries[userQueryKey]
    : userQueries[userQueryKey];
  const userIDs = useMemo(() => {
    if (selectedCompanyID) {
      return userQueryResult?.items?.map((item: { _id: string }) => item._id) || [];
    }

    return userQueryResult?.userIDs || [];
  }, [selectedCompanyID, userQueryResult]);
  const userOptions = useMemo<SelectBaseOption[]>(() => {
    return userIDs
      .map((userID: string) => {
        const user = users[userID];
        if (!user) {
          return null;
        }

        return {
          label: (
            <HStack className="items-center gap-2">
              <UserAvatarV2 size={16} user={user} />
              <div className="truncate">{user.name}</div>
            </HStack>
          ),
          value: user._id,
        };
      })
      .filter(isNotNil);
  }, [userIDs, users]);

  const company = useContext<Company>(CompanyContext);
  const companyQueryKey = getCompanyQueryKey({
    search: companySearchValue,
    userID: selectedUserID,
  });
  const companyQueryResult = thirdPartyCompanyQueries[companyQueryKey];
  const companyOptions = useMemo<SelectBaseOption[]>(
    () =>
      companyQueryResult?.companyIDs
        ?.map((companyID: string) => {
          const tpc = thirdPartyCompanies[companyID];
          if (!tpc) {
            return null;
          }

          return {
            valueLabel: tpc.name,
            label: (
              <CompanyOptionLabel
                company={tpc}
                hasCollectedMonthlySpend={company?.hasCollectedMonthlySpend ?? false}
                revenueTimeframe={company?.revenueTimeframe ?? RevenueTimeframes.mrr}
              />
            ),
            value: tpc.thirdPartyCompanyID,
          };
        })
        .filter(isNotNil) || [],
    [companyQueryResult, thirdPartyCompanies, company],
  );

  useEffect(() => {
    if (!autoAssignCompanyFlag) {
      return;
    }

    if (companyOptions.length === 1) {
      onCompanyChange(companyOptions[0].value);
    }

    if (companyOptions.length >= 1) {
      setAutoAssignCompanyFlag(false);
    }
  }, [autoAssignCompanyFlag, companyOptions, onCompanyChange]);

  return (
    <>
      <UserSelect
        isLoading={userLoading}
        onChange={onUserChange}
        onSearchChange={useDelayer(onUserSearchChange, 500)}
        options={userOptions}
        selectedUserID={selectedUserID}
      />
      <CompanySelect
        isLoading={companyLoading}
        onChange={onCompanyChange}
        onSearchChange={useDelayer(onCompanySearchChange, 500)}
        options={companyOptions}
        selectedCompanyID={selectedCompanyID}
      />
    </>
  );
};

const connectDispatchProps: ConnectDispatchProps = {
  searchUsers: queryUsers,
  searchUsersForCompany: queryUsersForCompany,
  searchCompanies: queryCompanies,
};

export default connect(
  (state: State): ConnectStateProps => ({
    thirdPartyCompanies: state.thirdPartyCompanies,
    thirdPartyCompanyQueries: state.thirdPartyCompanyQueries,
    users: Object.values(state.users).reduce(
      (acc: Record<string, User>, user) => {
        const typedUser = user as User;
        acc[typedUser._id] = typedUser;
        return acc;
      },
      {} as Record<string, User>,
    ),
    usersForCompanyQueries: state.usersForCompanyQueries,
    userQueries: state.userQueries,
  }),
  connectDispatchProps,
)(UserCompanySelect) as unknown as React.FC<OwnProps>;
